import axios from "axios";
import {
  userInfoCircuitBreaker,
  searchCreatorCircuitBreaker,
} from "utils/circuitBreaker";

const igBaseUrl = "/instagram-api";

// AbortController instances for cancellable requests
let userInfoAbortController = null;
let searchCreatorAbortController = null;

// Cancel any in-flight user-info request
export function cancelUserInfoRequest() {
  if (userInfoAbortController) {
    userInfoAbortController.abort();
    userInfoAbortController = null;
  }
}

// Cancel any in-flight search creator request
export function cancelSearchCreatorRequest() {
  if (searchCreatorAbortController) {
    searchCreatorAbortController.abort();
    searchCreatorAbortController = null;
  }
}

const lookupHashtag = async ({
  type = "recent-media",
  accountId,
  hashtagId,
  filterState = {},
  cursor = null,
}) => {
  const params = {};
  const filterKeys = Object.keys(filterState);

  if (cursor) {
    params["after"] = cursor;
  }

  if (filterKeys.length > 0) {
    filterKeys.forEach((key) => {
      if (filterState?.[key]?.length > 0) {
        params[key] = Array.isArray(filterState[key])
          ? filterState[key].join(",")
          : filterState[key];
      }
    });
  }

  return axios.get(`${igBaseUrl}/${accountId}/${type}/${hashtagId}`, {
    params,
  });
};
export default class instagramApi {
  constructor() {}

  async validateAccount(accountId) {
    const hashtagSearchUrl = `${igBaseUrl}/${accountId}/validate-token`;
    return axios.get(hashtagSearchUrl);
  }

  async getHashtag(accountId, hashtag) {
    const hashtagSearchUrl = `${igBaseUrl}/${accountId}/hashtag/${hashtag}`;
    return axios.get(hashtagSearchUrl);
  }

  async getRecentHashtagCodes(accountId) {
    const recSearchUrl = `${igBaseUrl}/${accountId}/cached-hashtags`;

    //Getting 60 for now, since this will allow up to 30 active + 30+ expired
    return axios.get(recSearchUrl, {
      params: { limit: 60 },
    });
  }

  async lookupHashtagRecentMedia({
    accountId,
    hashtagId,
    filterState = {},
    cursor = null,
  }) {
    return lookupHashtag({
      type: "recent-media",
      accountId,
      hashtagId,
      filterState,
      cursor,
    });
  }

  async lookupHashtagTopMedia({
    accountId,
    hashtagId,
    filterState = {},
    cursor = null,
  }) {
    return lookupHashtag({
      type: "top-media",
      accountId,
      hashtagId,
      filterState,
      cursor,
    });
  }

  async lookupMentionedMedia(accountId, cursor = null) {
    const hashtagId = "17843930926043558";
    const hashUrl = `${igBaseUrl}/${accountId}/recent-media/${hashtagId}${
      cursor ? `?after=${cursor}` : ""
    }`;

    return axios.get(hashUrl);
  }

  async lookupTaggedInMedia(accountId, cursor = null) {
    const hashUrl = `${igBaseUrl}/${accountId}/tags${
      cursor ? `?after=${cursor}` : ""
    }`;

    return axios.get(hashUrl);
  }

  async getRecentlySearchedCreators(accId) {
    // Check circuit breaker before making request
    if (userInfoCircuitBreaker.isOpen()) {
      console.warn(
        "[instagramApi] user-info circuit breaker is open, skipping request"
      );
      return null;
    }

    // Cancel any previous user-info request
    cancelUserInfoRequest();
    userInfoAbortController = new AbortController();

    return userInfoCircuitBreaker.execute(async () => {
      try {
        const { data } = await axios.get(`${igBaseUrl}/${accId}/user-info`, {
          signal: userInfoAbortController.signal,
        });
        return data;
      } catch (error) {
        // Don't throw if request was aborted (not a real failure)
        if (axios.isCancel(error) || error.name === "AbortError") {
          // Reset circuit breaker since this wasn't a real failure
          userInfoCircuitBreaker.reset();
          return null;
        }
        throw error;
      } finally {
        userInfoAbortController = null;
      }
    });
  }

  async searchCreators(accId, username, cursor) {
    // Check circuit breaker before making request
    if (searchCreatorCircuitBreaker.isOpen()) {
      console.warn(
        "[instagramApi] search-creator circuit breaker is open, skipping request"
      );
      return null;
    }

    // Cancel any previous search creator request
    cancelSearchCreatorRequest();
    searchCreatorAbortController = new AbortController();

    return searchCreatorCircuitBreaker.execute(async () => {
      try {
        const { data } = await axios.get(
          `${igBaseUrl}/${accId}/user-info/${username}`,
          {
            params: { withMedia: true, after: cursor },
            signal: searchCreatorAbortController.signal,
          }
        );
        return data;
      } catch (error) {
        // Don't throw if request was aborted (not a real failure)
        if (axios.isCancel(error) || error.name === "AbortError") {
          // Reset circuit breaker since this wasn't a real failure
          searchCreatorCircuitBreaker.reset();
          return null;
        }
        throw error;
      } finally {
        searchCreatorAbortController = null;
      }
    });
  }

  getRecentPosts(accountId, count, config = {}) {
    return axios
      .get("/instagram/recent", {
        ...config,
        params: { accountId: accountId, count: count || 9 },
      })
      .then(function (resp) {
        if (resp?.data?.error) {
          throw new Error(resp.data.error);
        }

        return resp.data;
      });
  }

  insertRecentPosts(recentPost) {
    return axios.post("/instagram/recent", recentPost).then(function (resp) {
      return resp.data;
    });
  }

  manuallyImportPost(post) {
    return axios.post("/instagram/manual", post).then(function (resp) {
      return resp.data;
    });
  }
}
